import {
  getRequiredMessage,
  MCustomNumberTypeRequired,
} from '@monetize/types/common';
import { z } from 'zod';
import { AddressSourceEnumZ } from './address.types';
import { CustomFieldRecordSchema } from './customFields.types';
import {
  NetTermsEnumZ,
  QuoteSettingsDefaultAddressSourceEnumZ,
} from './enums.types';
import { IOfferingRes } from './offering.types';

export const OneTimeOfferingProductRequestSchema = z.object({
  productId: z.string({
    error: (issue) => (issue.input === undefined ? 'Product' : undefined),
  }),
  productDescription: z.string({
    error: (issue) => (issue.input === undefined ? 'Product' : undefined),
  }),
  quantity: MCustomNumberTypeRequired(getRequiredMessage('Quantity')),
  unitPrice: MCustomNumberTypeRequired(getRequiredMessage('Price Per Unit')),
  isMandatory: z.boolean().default(false),
  isSelected: z.boolean().default(false),
});

export type IOneTimeOfferingProductRequestSchema = z.infer<
  typeof OneTimeOfferingProductRequestSchema
>;

export const OneTimeOfferingRequestSchema = z.object({
  offeringId: z.string(),
  offeringDescription: z.string(),
  startDate: z.string(),
  endDate: z.string().nullish(),
  products: z.array(OneTimeOfferingProductRequestSchema),
  /** UI only data point to store additional form state - never validated for form validity */
  metadata: z.any().nullish(),
});

export type IOneTimeOfferingRequestSchema = z.infer<
  typeof OneTimeOfferingRequestSchema
>;

export const OneTimeInvoiceFormSchema = z.object({
  billingContactId: z.string(),
  shippingContactId: z.string(),
  netTerms: NetTermsEnumZ,
  accountId: z.string(),
  currency: z.string(),
  legalEntityId: z.string(),
  addressInformation: z.object({
    addressSource: AddressSourceEnumZ,
    // FIXME: should be discriminated union based on addressSource these are required in ACCOUNT
    billingAddressId: z.string().nullish(),
    shippingAddressId: z.string().nullish(),
  }),
  billGroupId: z.string(),
  purchaseOrderNumber: z.string().max(255).nullish(),
  vatNumber: z.string().nullish(),
  registrationNumber: z.string().nullish(),
  invoicingDate: z.string(),
  dueDate: z.string().min(1, 'Due date is required'),
  invoiceTerms: z.string().nullish(),
  memo: z.string().nullish(),
  customFields: z.record(z.string(), z.any()).nullish(),
  offerings: z.tuple(
    [OneTimeOfferingRequestSchema],
    OneTimeOfferingRequestSchema,
  ),
});

export type IOneTimeInvoiceFormSchema = z.infer<
  typeof OneTimeInvoiceFormSchema
>;

export interface IOneTimeOfferingRequestSchemaUi extends IOneTimeOfferingRequestSchema {
  metadata: {
    loading: boolean;
    offering?: IOfferingRes | null;
  };
}

/**
 * The form state has additional data to store the offering and products so
 * that it does not need to be passed around to child components and so that
 * the loading state can be stored with each row.
 */
export interface IOneTimeInvoiceFormSchemaWithOfferings extends Omit<
  IOneTimeInvoiceFormSchema,
  'offerings'
> {
  offerings: Array<IOneTimeOfferingRequestSchemaUi>;
}

const InvoiceItemsRequest = z.tuple(
  [
    z.object({
      offeringId: z.string(),
      offeringDescription: z.string(),
      startDate: z.string(),
      endDate: z.string().nullish(),
      productId: z.string({
        error: (issue) => (issue.input === undefined ? 'Product' : undefined),
      }),
      productDescription: z.string().nullish(),
      quantity: MCustomNumberTypeRequired(getRequiredMessage('Quantity')),
      unitPrice: MCustomNumberTypeRequired(
        getRequiredMessage('Price Per Unit'),
      ),
      position: z.number(),
    }),
  ],
  z.object({
    offeringId: z.string(),
    offeringDescription: z.string(),
    startDate: z.string(),
    endDate: z.string().nullish(),
    productId: z.string({
      error: (issue) => (issue.input === undefined ? 'Product' : undefined),
    }),
    productDescription: z.string().nullish(),
    quantity: MCustomNumberTypeRequired(getRequiredMessage('Quantity')),
    unitPrice: MCustomNumberTypeRequired(getRequiredMessage('Price Per Unit')),
    position: z.number(),
  }),
);

export type IInvoiceItemsRequest = z.infer<typeof InvoiceItemsRequest>;

export const OneTimeInvoiceRequestSchema = OneTimeInvoiceFormSchema.omit({
  offerings: true,
}).extend({
  invoiceItems: InvoiceItemsRequest,
});

export type IOneTimeInvoiceRequestSchema = z.infer<
  typeof OneTimeInvoiceRequestSchema
>;

export const OneTimeInvoicePatchSchema = z.object({
  purchaseOrderNumber: z.string().nullish(),
  vatNumber: z.string().nullish(),
  registrationNumber: z.string().nullish(),
  netTerms: NetTermsEnumZ,
  billingContactId: z.string(),
  shippingContactId: z.string(),
  locale: z.string().nullish(),
  memo: z.string().nullable(),
  invoiceTerms: z.string().nullish(),
  invoicingDate: z.string(),
  dueDate: z.string(),
  addressInformation: z.object({
    addressSource: QuoteSettingsDefaultAddressSourceEnumZ,
    billingAddressId: z.string().nullish(),
    shippingAddressId: z.string().nullish(),
  }),
  customFields: CustomFieldRecordSchema,
});

export type IOneTimeInvoicePatchSchema = z.infer<
  typeof OneTimeInvoicePatchSchema
>;

/**
 * Once a one-time invoice leaves DRAFT, the API only accepts this subset of
 * header fields. Anything else (net terms, contacts, addresses, dates) is
 * immutable at that point and must not be sent.
 */
export const OneTimeInvoicePostDraftPatchSchema =
  OneTimeInvoicePatchSchema.pick({
    purchaseOrderNumber: true,
    vatNumber: true,
    registrationNumber: true,
    memo: true,
    invoiceTerms: true,
    customFields: true,
  });

export type IOneTimeInvoicePostDraftPatchSchema = z.infer<
  typeof OneTimeInvoicePostDraftPatchSchema
>;
